<script lang="ts">
    import {aceEditor} from 'shared/ace-editor';
    import {clickOutside, enforceFocusWithin, enforceModalOpen} from 'shared/svelte-actions';
    import {getLogger} from 'src/core';
    import type {
        TextValueEditorLocalizations,
        TextValueEditorOptions,
        TextValueEditorValidations,
        TTextValueEditorType
    } from './types';
    import type {FormControlOptions} from '../../forms/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {tick} from 'svelte';
    import {onDestroy} from 'svelte';
    import {FormControlBuilder} from '../../forms/form-control';
    import {validatorFactory, addPrefixAndSuffix, trimPrefixAndSuffix} from './utils';
    import {formControls} from '../../forms/use.form-controls';
    import {style} from 'svelte-forms';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {getContext} from 'svelte';
    import {createInputDescription} from '../../description/utils';
    import {emptyAsNullFormatter, emptyAsNullParser, trim} from '../_shared/editors-utils';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {required} from '../../forms/use.required';
    import {disabledAttribute} from '../../shared/use.disabled-attribute';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import TextualEditorsContextMenuWrapper from 'shared/value-editors/internal/editors/_shared/TextualEditorsContextMenuWrapper.svelte';
    import KpIconButton from 'shared/ui-widgets/button/KpIconButton.svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {isString} from 'shared/utils/string-utils';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: TextValueEditorOptions;
    export let validations: TextValueEditorValidations;

    const TEXT_INPUTS = [
        'text',
        'email',
        'url',
        'tel'
    ];

    const localize: EditorsLocalizationFunction<TextValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const labelContext = getValueEditorLabelContext();

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations, options.type))
        .withOptions(formControlOptions)
        .withParsers(hasPrefixOrSuffix(options) ? [addPrefixAndSuffix(options)] : [])
        .withModifiers([trim(options), emptyAsNullParser(options)])
        .withFormatters([...hasPrefixOrSuffix(options) ? [trimPrefixAndSuffix(options)] : [], emptyAsNullFormatter(options)])
        .build(getLogger());

    const fieldValue$ = formControl.getFieldStateAsStore();

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    let inputElement: HTMLInputElement | HTMLTextAreaElement | undefined;
    let isEditorInFullscreen = false;

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});
    $: aceEditorOptions = {...options.aceOptions, placeholder, readOnly: isDisabled, isFocused};

    $: activeSubType = selectActiveSubType(activeSubType, $fieldValue$.value, options);

    onDestroy(() => {
        subscription.unsubscribe();
    });

    function selectActiveSubType(currentSubType: TTextValueEditorType, viewValue: string, currentOptions: TextValueEditorOptions) {
        const newSubType = resolveSubType(viewValue, currentOptions);
        if (newSubType !== currentSubType) {
            refocusInputElementAfterDomUpdate(inputElement === document.activeElement, inputElement?.selectionStart);
        }
        return newSubType;
    }

    function resolveSubType(viewValue: string, currentOptions: TextValueEditorOptions) {
        if (currentOptions.switchToTextareaThreshold === 0) {
            return currentOptions.type;
        }
        if (currentOptions.type !== 'text' && currentOptions.type !== 'textarea') {
            return currentOptions.type;
        }
        if (hasPrefixOrSuffix(currentOptions)) {
            return currentOptions.type;
        }
        if (!isString(viewValue)) {
            return currentOptions.type;
        }
        if (viewValue.length >= currentOptions.switchToTextareaThreshold) {
            return 'textarea';
        } else {
            return currentOptions.type;
        }
    }

    function refocusInputElementAfterDomUpdate(inputElementHasFocus: boolean, cursorPosition: number) {
        tick().then(() => {
            if (exists(inputElement) && inputElementHasFocus) {
                inputElement.setSelectionRange(cursorPosition, cursorPosition);
                inputElement.focus();
            }
        });
    }

    function isTextInput(type: TTextValueEditorType): boolean {
        return TEXT_INPUTS.includes(type);
    }

    function countRowsInString(str: string = ''): number {
        const rowsCount = ((str.match(/\n/g) || []).length) + 1;

        if (rowsCount === 1) {
            return Math.floor(str.length / 60) + 1;
        }

        return rowsCount;
    }

    function getNumberOfRows(value: string, minRows: number = 1, maxRows: number = 10): number {
        if (value) {
            return Math.min(Math.max(countRowsInString(value), minRows), maxRows);
        }
        return minRows;
    }

    function hasPrefixOrSuffix(opts: TextValueEditorOptions) {
        return opts.prefix || opts.suffix;
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

{#if isTextInput(activeSubType) && hasPrefixOrSuffix(options)}
    <TextualEditorsContextMenuWrapper {editorId}
                                      {inputElement}
                                      let:specialSymbolsKeyboardAction>

        <div class="input-group input-group-{size}" role="group">
            {#if options.prefix}
                <div class="input-group-addon prefix">{options.prefix}</div>
            {/if}

            <input class="form-control input-{size}"

                   type={options.type}
                   id={editorId}
                   name={editorName}
                   {placeholder}
                   disabled={isDisabled}
                   maxlength={validations.maxlength}
                   minlength={validations.minlength}
                   use:required={validations.required}
                   use:focusOnMount={isFocused}

                   use:formControls={formControl}
                   use:touched={{formControl}}
                   use:style={{field: formControl.getFieldStateAsStore()}}
                   use:ariaInvalid={formControl}
                   use:ariaErrormessage={formControl}
                   use:ariaDescribedby={inputDescription}
                   use:specialSymbolsKeyboardAction
                   bind:this={inputElement}

                   data-main-input/>

            {#if options.suffix}
                <div class="input-group-addon suffix">{options.suffix}</div>
            {/if}
        </div>
    </TextualEditorsContextMenuWrapper>
{/if}

{#if isTextInput(activeSubType) && !hasPrefixOrSuffix(options)}
    <TextualEditorsContextMenuWrapper {editorId}
                                      {inputElement}
                                      let:specialSymbolsKeyboardAction>

        <input class="form-control input-{size}"

               type={options.type}
               id={editorId}
               name={editorName}
               {placeholder}
               disabled={isDisabled}
               maxlength={validations.maxlength}
               minlength={validations.minlength}
               use:required={validations.required}
               use:focusOnMount={isFocused}

               use:formControls={formControl}
               use:touched={{formControl}}
               use:style={{field: formControl.getFieldStateAsStore()}}
               use:ariaInvalid={formControl}
               use:ariaErrormessage={formControl}
               use:ariaDescribedby={inputDescription}
               use:specialSymbolsKeyboardAction
               bind:this={inputElement}

               data-main-input/>
    </TextualEditorsContextMenuWrapper>
{/if}

{#if activeSubType === 'textarea'}
    <TextualEditorsContextMenuWrapper {editorId}
                                      {inputElement}
                                      let:specialSymbolsKeyboardAction>

        <textarea class="form-control input-{size}"

                  id={editorId}
                  name={editorName}
                  {placeholder}
                  disabled={isDisabled}
                  maxlength={validations.maxlength}
                  minlength={validations.minlength}
                  use:required={validations.required}
                  use:focusOnMount={isFocused}
                  use:touched={{formControl}}
                  rows={getNumberOfRows($fieldValue$.value)}

                  use:formControls={formControl}
                  use:style={{field: formControl.getFieldStateAsStore()}}
                  use:ariaInvalid={formControl}
                  use:ariaErrormessage={formControl}
                  use:ariaDescribedby={inputDescription}
                  use:specialSymbolsKeyboardAction
                  bind:this={inputElement}

                  data-main-input>
        </textarea>
    </TextualEditorsContextMenuWrapper>
{/if}

{#if activeSubType === 'rich-textarea'}
    <div class="ace-editor-wrapper"
         class:fullscreen-editor={isEditorInFullscreen}
         use:enforceModalOpen={{isModalOpen: isEditorInFullscreen}}
         use:enforceFocusWithin={{disabled: !isEditorInFullscreen}}
         use:clickOutside={{disabled: !isEditorInFullscreen}}
         on:click-outside={() => isEditorInFullscreen = false}>

        <div class="form-control ace-editor"
             id={editorId}
             data-name={editorName}

             use:disabledAttribute={isDisabled}
             use:aceEditor={{formControl, options: aceEditorOptions}}
             use:touched={{formControl, touchTriggerEvent: 'ace-blur'}}
             use:style={{field: formControl.getFieldStateAsStore()}}
             use:ariaInvalid={formControl}
             use:ariaErrormessage={formControl}
             use:ariaDescribedby={inputDescription}
             use:ariaLabelledby={labelContext}

             data-main-input>
        </div>

        <div class="fullscreen-button-container">
            <KpIconButton icon={isEditorInFullscreen ? 'compress-alt' : 'expand'}
                          on:click={() => isEditorInFullscreen = !isEditorInFullscreen}/>
        </div>
    </div>
{/if}

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>

<Description {inputDescription}/>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .ace-editor-wrapper {
        position: relative;

        &.fullscreen-editor {
            position: fixed;
            top: @spacing-xl;
            bottom: @spacing-xl;
            left: @spacing-xxl;
            right: @spacing-xxl;
            z-index: @modal-z-index;
            border-radius: @border-radius-large;
            box-shadow: rgba(0, 0, 0, 0.25) 0 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px;

            .ace-editor {
                height: 100%;
            }
        }

        .ace-editor {
            min-height: 300px;
        }

        .fullscreen-button-container {
            position: absolute;
            top: @spacing-m;
            right: @spacing-ml;
        }
    }

    textarea {
        resize: vertical;
    }

    textarea.input-xs {
        padding: 2px 5px;
        font-size: 12px;
        line-height: 1.5;
        border-radius: 3px;
        height: auto;
    }
</style>